import { useEffect, useState } from "react";
import { useVoice } from "@/contexts/VoiceAssistantContext";
import { X, ChevronLeft, ChevronRight, HelpCircle, Sparkle, EyeOff } from "lucide-react";

/**
 * Reusable contextual mini-tutorial.
 *
 * Auto-opens the first time (per storageKey). The user can close it with
 * "Entendido" or "No volver a mostrar" (both persist). A small "?" tab on the
 * right edge of the screen re-opens it on demand.
 *
 * @param {string} storageKey localStorage key (e.g. "ca_tut_vidente")
 * @param {string} title       header title
 * @param {{emoji: string, title: string, desc: string}[]} steps
 */
export const MiniTutorial = ({ storageKey, title, steps }) => {
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(() => {
    try { return !localStorage.getItem(storageKey); } catch { return false; }
  });
  const voice = useVoice();

  const close = () => {
    try { localStorage.setItem(storageKey, "done"); } catch { /* noop */ }
    voice.stop();
    setOpen(false);
    setStep(0);
  };

  useEffect(() => {
    if (!open || !voice.isOn || !steps[step]) return undefined;
    const cur = steps[step];
    const t = setTimeout(() => voice.speak(`${cur.title}. ${cur.desc}`, { id: `${storageKey}:${step}` }), 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, voice.isOn]);

  if (!open) {
    return (
      <button
        data-testid={`minitut-help-${storageKey}`}
        onClick={() => { setStep(0); setOpen(true); }}
        title="Ver tutorial de esta sección"
        className="fixed top-1/2 -translate-y-1/2 right-0 z-[104] bg-black/70 border border-[#d4af37]/40 border-r-0 rounded-l-xl px-1.5 py-3 text-[#d4af37] hover:bg-[#d4af37]/15 backdrop-blur"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
    );
  }

  const s = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div
      data-testid={`minitut-overlay-${storageKey}`}
      className="fixed inset-0 z-[115] flex items-center justify-center px-4 py-8 bg-black/80 backdrop-blur-sm notranslate"
      translate="no"
    >
      <div className="relative max-w-md w-full glass-dark border border-[#d4af37]/30 rounded-3xl p-6 shadow-2xl">
        <button
          data-testid="minitut-close"
          onClick={close}
          className="absolute top-3 right-3 text-neutral-400 hover:text-white"
          aria-label="Cerrar tutorial"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37] mb-3 flex items-center gap-1.5">
          <Sparkle className="w-3.5 h-3.5" /> {title}
        </div>

        {/* progress */}
        <div className="flex items-center gap-1 mb-5">
          {steps.map((_, i) => (
            <div key={i} className={`h-1 rounded-full flex-1 transition-all ${i <= step ? "bg-[#d4af37]" : "bg-white/10"}`} />
          ))}
        </div>

        <div className="text-center mb-6">
          <div className="text-4xl mb-3">{s.emoji}</div>
          <h3 className="font-serif text-xl text-white mb-2" data-testid="minitut-title">{s.title}</h3>
          <p className="text-sm text-neutral-300 leading-relaxed" data-testid="minitut-desc">{s.desc}</p>
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            data-testid="minitut-prev"
            onClick={() => setStep((x) => Math.max(0, x - 1))}
            disabled={step === 0}
            className="text-xs text-neutral-400 hover:text-white disabled:opacity-30 flex items-center gap-1 px-2 py-2"
          >
            <ChevronLeft className="w-4 h-4" /> Anterior
          </button>
          <div className="text-[10px] text-neutral-500 font-mono">{step + 1}/{steps.length}</div>
          {!isLast ? (
            <button
              data-testid="minitut-next"
              onClick={() => setStep((x) => Math.min(steps.length - 1, x + 1))}
              className="btn-gold rounded-full uppercase text-xs tracking-widest px-4 py-2 flex items-center gap-1"
            >
              Siguiente <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="minitut-finish"
              onClick={close}
              className="btn-gold rounded-full uppercase text-xs tracking-widest px-4 py-2"
            >
              ¡Entendido!
            </button>
          )}
        </div>

        <button
          data-testid="minitut-never-show"
          onClick={close}
          className="mt-4 w-full text-[11px] text-neutral-500 hover:text-neutral-300 flex items-center justify-center gap-1.5"
        >
          <EyeOff className="w-3 h-3" /> No volver a mostrar esta ventana
        </button>
      </div>
    </div>
  );
};
